/** Presentation helpers for the read-only portal (pure, unit-tested). */

/** Human file size (bytes → e.g. "2.0 KB"). Never throws on odd input. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

/** A calendar date from an ISO string (locale-independent YYYY-MM-DD). '' → '—'. */
export function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

/** A date + minute stamp from an ISO string. '' → '—'. */
export function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

/** Turn an enum-ish token ('report_available', 'tax_audit') into a Title Case label. */
export function humanize(token: string): string {
  return token
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** The design-system §10 badge tone for a client-safe case / request status token. */
export type StatusTone =
  | 'open'
  | 'in-progress'
  | 'success'
  | 'warning'
  | 'danger'
  | 'muted'
  | 'neutral';

export function statusTone(status: string): StatusTone {
  switch (status.toLowerCase().replace(/-/g, '_')) {
    case 'open':
    case 'submitted':
      return 'open';
    case 'in_progress':
    case 'active':
      return 'in-progress';
    case 'closed':
    case 'completed':
    case 'fulfilled':
      return 'success';
    case 'on_hold':
    case 'pending':
      return 'warning';
    case 'refused':
    case 'rejected':
      return 'danger';
    case 'archived':
      return 'muted';
    default:
      return 'neutral';
  }
}

/** Up to two initials from a display name ('Ada Client' → 'AC'); '' → '?'. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
}
